import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { PenLine } from "lucide-react";

export default function EditorLogin() {
  const { login, user, loading } = useEditorAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/editor" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/editor", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="editor-login-page">
      <aside className="hidden lg:flex bg-[#0A1128] text-white grain relative">
        <div className="px-14 py-24 flex flex-col justify-between w-full">
          <div>
            <img src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/cver0rlc_HappyHomeAgency%20Logo2026.png" alt="Happy Home Agency" className="h-20 w-auto mb-8 bg-white p-2"/>
            <div className="overline text-[#688A4E] mb-4">Editor Portal</div>
            <h1 className="font-serif text-5xl leading-tight">Happy Home<br/>Blog Editor.</h1>
            <p className="text-[#A1A9BC] mt-6 max-w-sm leading-relaxed">Publish stories, guides and updates that build trust and traffic for Happy Home Agency.</p>
          </div>
          <div className="text-xs text-[#A1A9BC]">© {new Date().getFullYear()} Happy Home Agency · Editor Portal</div>
        </div>
      </aside>
      <section className="flex items-center justify-center px-6 sm:px-14 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <div className="overline mb-3">Sign in</div>
          <h2 className="font-serif text-4xl text-[#0A1128] mb-2">Welcome back.</h2>
          <p className="text-gray-500 text-sm mb-10">For content editors only.</p>
          <div className="space-y-6">
            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Email</label>
              <input data-testid="editor-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Password</label>
              <input data-testid="editor-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            </div>
            {err && <div data-testid="editor-login-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2">{err}</div>}
            <button type="submit" disabled={submitting} data-testid="editor-login-submit" className="btn-primary w-full justify-center disabled:opacity-60">
              <PenLine className="w-4 h-4"/> {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
